import React, { ReactElement, useEffect, useState } from 'react';
import { space } from '@op/opds.tokens';
import { Message, useMessage } from '@messageformat/react';
import { Row, Alert, Tabs, Tab } from '@op/opds.opux';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import CourseTaskHeader from '../CourseTaskHeader';
import Selections from './DiversityAnalyzer/Selections';
import {
  tab1Products,
  tab2Products,
  riskProfiles,
  tab3Products,
} from './TaskOne/taskoneoptions';
import RiskProfiles from './TaskOne/RiskProfiles';
import { useIsMobile } from '../../util/mediaQuery';
import { ResultTileData } from './interfaces/interfaces';
import ResultTile from './TaskOne/ResultTile';
import parseMsgContent from '../../util/parseMsgContent';
import CourseContent from '../CourseContent';

interface InnerOptionItem {
  name: JSX.Element;
  share: number;
}

interface OptionItem {
  id: number;
  name: JSX.Element;
  description?: JSX.Element;
  icon?: JSX.Element;
  type?: string;
  instruments: InnerOptionItem[];
  locations: InnerOptionItem[];
  fields: InnerOptionItem[];
}

interface SelectedItem extends OptionItem {
  investment: number;
}

interface GroupedData {
  type: string;
  investment: number;
}

const TaskOne = (): ReactElement => {
  const [selectedValue, setSelectedValue] = React.useState('');
  const [selected, setSelected] = useState<SelectedItem[]>([]);
  const [correctPortfolio, setCorrectPortfolio] = useState(false);
  const [donutData, setDonutData] = useState<ResultTileData[]>([]);
  const isMobile = useIsMobile();

  const firstIntro = useMessage('course7.courseContent.part2');
  const parsedFirstIntro = parseMsgContent(firstIntro);

  const stockInvestment = 'course7.task1.riskprofileGroups.StockInvestment';
  const fixedIncomeInvestment = 'course7.task1.riskprofileGroups.FixedIncomeInvestment';
  const alternativeInvestment = 'course7.task1.riskprofileGroups.AlternativeInvestment';

  const tab1Label = useMessage('course7.task1.products.tab1.label');
  const tab2Label = useMessage('course7.task1.products.tab2.label');
  const tab3Label = useMessage('course7.task1.products.tab3.label');
  const resultTileTitle = useMessage('course7.task1.resultTileTitle');
  const userTileTitle = useMessage('course7.task1.userTileTitle');
  const choosedPortfolio = useMessage('course7.task1.choosedPortfolio');
  const choosedRiskProfile = useMessage('course7.task1.choosedRiskProfile');

  const calculatePercentage = (number: number, total: number): number => {
    if (total === 0) return 0;
    return parseFloat(((number / total) * 100).toFixed(2));
  };

  const compareArrays = React.useCallback(
    (
      array1: { type: string; investment: number }[],
      array2: ResultTileData[],
    ): boolean => {
      const areNumbersWithin5Percent = (num1: number, num2: number): boolean => {
        const difference = Math.abs(num1 - num2);
        return difference <= 5;
      };

      const calculateTotalInvestment = (array: { investment: number }[]): number =>
        array.reduce((sum, item) => sum + item.investment, 0);

      const totalInvestment1 = calculateTotalInvestment(array1);
      const totalInvestment2 = array2.reduce((sum, item) => sum + item.value, 0);

      const calculatePercentages = (
        array: { type: string; investment: number }[],
        total: number,
      ): { type: string; percentage: number }[] =>
        array.map((item) => ({
          type: item.type,
          percentage: (item.investment / total) * 100,
        }));

      const array1Percentages = calculatePercentages(array1, totalInvestment1);
      const array2Percentages = array2.map((item) => ({
        type: item.type,
        percentage: (item.value / totalInvestment2) * 100,
      }));

      if (array1Percentages.length !== array2Percentages.length) {
        return false;
      }

      return array1Percentages.every((item1) => {
        const item2 = array2Percentages.find((item) => item.type === item1.type);
        return item2 && areNumbersWithin5Percent(item1.percentage, item2.percentage);
      });
    },
    [],
  );

  const getRiskProfileLabelByType = React.useCallback(
    (type: string): string => {
      if (type === 'stock') {
        return stockInvestment;
      }
      if (type === 'fixedincome') {
        return fixedIncomeInvestment;
      }
      if (type === 'alternative') {
        return alternativeInvestment;
      }
      return '';
    },
    [stockInvestment, fixedIncomeInvestment, alternativeInvestment],
  );

  useEffect(() => {
    let accumulator = 0;
    const groupedByType: GroupedData[] = [
      { type: 'fixedincome', investment: 0 },
      { type: 'stock', investment: 0 },
      { type: 'alternative', investment: 0 },
    ];

    selected.forEach((val) => {
      accumulator += val.investment;
      const existingGroup = groupedByType.find((group) => group.type === val.type);
      if (existingGroup) {
        existingGroup.investment += val.investment;
      }
    });

    // Ensure that groups with no investments are set to 0
    groupedByType.forEach((group, index) => {
      if (!selected.some((val) => val.type === group.type)) {
        groupedByType[index] = { ...group, investment: 0 };
      }
    });

    const userSelectedDonut = groupedByType.map((group) => ({
      label: getRiskProfileLabelByType(group.type),
      value: calculatePercentage(group.investment, accumulator),
      type: group.type,
    }));

    if (groupedByType.some((group) => group.investment > 0)) {
      setCorrectPortfolio(
        compareArrays(groupedByType, riskProfiles[parseInt(selectedValue, 10)].data),
      );
      setDonutData(userSelectedDonut);
    } else {
      setDonutData([]);
      setCorrectPortfolio(false);
    }
  }, [compareArrays, getRiskProfileLabelByType, selected, selectedValue]);

  return (
    <div style={{ marginBottom: space.xl }}>
      <CourseTaskHeader questionNumber={1} totalQuestions={3} />
      <h3
        className="opds-styles-v1-text-hero-small"
        style={{ textAlign: 'center', marginTop: space.xxl, marginBottom: space.default }}
      >
        <Message id="course7.task1.title" />
      </h3>

      <CourseContent content={parsedFirstIntro} />
      <h4
        className="opds-styles-v1-text-title-large"
        style={{
          textAlign: 'center',
          paddingTop: space.small,
          paddingBottom: space.default,
        }}
      >
        <Message id="course7.task1.question1.title" />
      </h4>
      <RiskProfiles selectedValue={selectedValue} setSelectedValue={setSelectedValue} />
      {selectedValue ? (
        <>
          <h4
            data-testid="course7-portfolio-tabs-title"
            className="opds-styles-v1-text-title-large"
            style={{
              textAlign: 'center',
              paddingTop: space.small,
              paddingBottom: 0,
            }}
          >
            <Message id="course7.task1.question2.title" />
          </h4>
          <p
            className="opds-styles-v1-text-body-medium"
            style={{
              textAlign: 'center',
              paddingTop: space.default,
            }}
          >
            <Message id="course7.task1.question2.body" />
          </p>
          <Tabs
            small
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Tab label={tab1Label}>
              <Selections
                selected={selected}
                setSelected={setSelected}
                options={tab1Products}
                type="FUND"
              />
            </Tab>
            <Tab label={tab2Label}>
              <Selections
                selected={selected}
                setSelected={setSelected}
                options={tab2Products}
                type="FUND"
              />
            </Tab>
            <Tab label={tab3Label}>
              <Selections
                selected={selected}
                setSelected={setSelected}
                options={tab3Products}
                type="FUND"
              />
            </Tab>
          </Tabs>

          {!isMobile ? (
            <Row style={{ marginTop: space.default, marginLeft: 0, marginRight: 0 }}>
              <ResultTile title={resultTileTitle} data={donutData} />
              <ResultTile
                title={userTileTitle}
                data={riskProfiles[parseInt(selectedValue, 10)].data}
              />
            </Row>
          ) : (
            <Tabs
              small
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Tab label={choosedPortfolio}>
                <ResultTile title={choosedPortfolio} data={donutData} />
              </Tab>
              <Tab label={choosedRiskProfile}>
                <ResultTile
                  title={userTileTitle}
                  data={riskProfiles[parseInt(selectedValue, 10)].data}
                />
              </Tab>
            </Tabs>
          )}
        </>
      ) : null}
      {correctPortfolio ? (
        <Alert type="warning">
          <Message id="course7.task1.correctportfolio" />
        </Alert>
      ) : null}
      {selectedValue ? (
        <p
          className="opds-styles-v1-text-label-small"
          style={{ display: 'flex', paddingBottom: space.default, textAlign: 'left' }}
        >
          <IconInformation />
          <Message id="course7.task1.notification" />
        </p>
      ) : null}
    </div>
  );
};

export default TaskOne;
